import { useDeferredValue, useMemo, useState } from 'react'
import { ActionGlyph } from '../../components/ActionGlyph'
import { TourCampaignPanel } from '../../components/TourCampaignPanel'
import { TourMark } from '../../components/TourMark'
import { useAppActions, useAppState } from '../../state/AppProvider'
import { EmptyState, PageMast, SectionHeading } from '../home/FanPageChrome'
import {
  formatShortDate,
  showStatusLabel,
  sortedShows,
  type Navigate,
} from '../home/fanExperience'
import '../home/fanExperience.css'

interface TourPageProps {
  authenticated?: boolean
  navigate: Navigate
}

type TourFilter = 'all' | 'upcoming' | 'saved' | 'check-in'

const tourFilters: ReadonlyArray<{ id: TourFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'saved', label: 'Saved' },
  { id: 'check-in', label: 'Check-in' },
]

export function TourPage({ authenticated = true, navigate }: TourPageProps) {
  const state = useAppState()
  const actions = useAppActions()
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<TourFilter>(authenticated && state.preferences.savedShowsOnly ? 'saved' : 'all')
  const [region, setRegion] = useState('all')
  const deferredQuery = useDeferredValue(query.trim().toLowerCase())
  const regions = useMemo(
    () => Array.from(new Set(state.shows.map((show) => show.region))).sort(),
    [state.shows],
  )

  const filteredShows = useMemo(() => sortedShows(state.shows).filter((show) => {
    const matchesQuery = !deferredQuery || `${show.city} ${show.region} ${show.venue}`.toLowerCase().includes(deferredQuery)
    const matchesRegion = region === 'all' || show.region === region
    const matchesFilter = filter === 'all'
      || (filter === 'upcoming' && show.showStatus !== 'complete')
      || (authenticated && filter === 'saved' && state.savedShowIds.includes(show.id))
      || (authenticated && filter === 'check-in' && show.checkInOpen)
    return matchesQuery && matchesRegion && matchesFilter
  }), [authenticated, deferredQuery, filter, region, state.savedShowIds, state.shows])

  const availableFilters = authenticated
    ? tourFilters
    : tourFilters.filter((item) => item.id === 'all' || item.id === 'upcoming')

  return (
    <div className="fan-page fan-tour">
      <PageMast
        eyebrow="Full route"
        navigate={navigate}
        title="Tour stops"
        trailing={(
          <button className="fan-text-button" onClick={() => navigate(authenticated ? '/itinerary' : '/auth/sign-in')} type="button">
            {authenticated ? 'My plan' : 'Sign in'}
          </button>
        )}
      />

      <TourCampaignPanel />

      {!authenticated ? (
        <section className="fan-guest-note" aria-label="Guest access">
          <div>
            <p className="fan-eyebrow">Browsing as guest</p>
            <p>Explore the route now. Sign in only when you want to save a stop or build a show-day plan.</p>
          </div>
          <button className="fan-button fan-button--ink" onClick={() => navigate('/auth/sign-in')} type="button">
            Sign in for planning
          </button>
        </section>
      ) : null}

      <section className="fan-search-panel" aria-label="Find tour stops">
        <label className="fan-search-field">
          <span className="sr-only">Search city or venue</span>
          <TourMark className="fan-search-field__mark" name="search" />
          <input
            autoComplete="off"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search city or venue"
            type="search"
            value={query}
          />
        </label>
        <label className="fan-select-field">
          <span>Region</span>
          <select onChange={(event) => setRegion(event.target.value)} value={region}>
            <option value="all">Everywhere</option>
            {regions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
      </section>

      <nav aria-label="Tour filters" className="fan-filter-bar">
        {availableFilters.map((item) => (
          <button
            aria-pressed={filter === item.id}
            className={filter === item.id ? 'is-active' : ''}
            key={item.id}
            onClick={() => setFilter(item.id)}
            type="button"
          >
            {item.label}
          </button>
        ))}
      </nav>

      <section aria-label="Tour stop results" className="fan-tour__results">
        <SectionHeading
          count={filteredShows.length}
          eyebrow={filter === 'saved' ? 'Your route' : 'Tour ledger'}
          title={deferredQuery ? `Results for “${query.trim()}”` : 'Stops'}
        />
        {filteredShows.length ? (
          <ol className="fan-stop-list">
            {filteredShows.map((show) => {
              const isSaved = authenticated && state.savedShowIds.includes(show.id)
              const isSelected = authenticated && state.selectedShowId === show.id
              return (
                <li className={`fan-stop-row ${isSelected ? 'is-selected' : ''}`} key={show.id}>
                  <button
                    className="fan-stop-row__main"
                    onClick={() => {
                      if (authenticated) actions.selectShow(show.id)
                      navigate(`/shows/${show.id}`)
                    }}
                    type="button"
                  >
                    <span className="fan-stop-row__sequence">{String(show.sequence).padStart(2, '0')}</span>
                    <span className="fan-stop-row__date">{formatShortDate(show.isoDate)}</span>
                    <span className="fan-stop-row__copy">
                      <strong>{show.city}</strong>
                      <small>{show.venue}</small>
                      <em className={`fan-stop-status fan-stop-status--${show.showStatus}`}>{showStatusLabel(show)}</em>
                    </span>
                    <span className="fan-stop-row__arrow"><ActionGlyph direction="external" /></span>
                  </button>
                  {authenticated ? (
                    <button
                      aria-label={`${isSaved ? 'Remove' : 'Save'} ${show.city}`}
                      aria-pressed={isSaved}
                      className="fan-save-button fan-save-button--stop"
                      onClick={() => actions.toggleSavedShow(show.id)}
                      type="button"
                    >
                      <TourMark name={isSaved ? 'check' : 'route'} />
                    </button>
                  ) : null}
                </li>
              )
            })}
          </ol>
        ) : (
          <EmptyState
            actionLabel="Clear filters"
            body="Try another city, venue, region, or saved-stop filter."
            onAction={() => {
              setQuery('')
              setFilter('all')
              setRegion('all')
            }}
            title="No stops match"
          />
        )}
      </section>
    </div>
  )
}
